/**
 * search-panel-alternative.component.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import {
  animate,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  style,
  transition,
  trigger
} from "@angular/core";
import { AbstractControl, FormControl, FormGroup } from "@angular/forms";

import { AlertService } from "../common/alert.service";
import { TypedData, TypedDataHolder } from "../common/data/typed-data";
import { Subject } from "../subjects/subject";
import { SubjectContextType } from "../subjects/subject-context-type";
import { empty, valueless } from "../support/utilities/functions";

import { isInequality, Operator, OperatorString, MagicOperator, PastXDays } from "./operators";
import { FilterNode, PartitionNode, SearchNode, SearchTerm, SearchRoot }
  from "./search-node";
import { SearchService } from "./search.service";

const ANIMATION_SPEED_IN : number = 300; // TODO: to the config
const ANIMATION_SPEED_OUT : number = 300; // TODO: to the config

/**
 * 
 */
@Component({
  selector: "search-panel-alternative",
  template: `
    <div [class.well]="!unwrap">
      <div *ngIf="!enforcedType" class="form-group well-top">
        <select [formControl]="currentIndex" class="form-control">
          <option
            *ngFor="let opt of opts"
            class="uppercase"
            [ngValue]="opt">
            {{displayIndex(opt)}}
          </option>
        </select>
        <hr>
      </div>

      <p class="debug-only"><em>
        This is a preview of an experimental interface. It is expected to have
        bugs or other oddities. Please only use this for testing and feedback.
      </em></p>

      <template [ngIf]="partitions.length > 0">
        <h4>Search Terms</h4>
      </template>
      <div
        *ngFor="let partition of partitions; let i = index"
        class="row fieldEntry searchTerm"
        @fadeInOut>
        <div class="col-xs-12" #term>
          <label>
            {{partition.displayName | uppercase}}
            <div
              *ngIf="allowsFreeTextOverride.has(partition.name)"
              class="pull-right">
              <button
                class="btn btn-default btn-xs"
                (click)="term.forceFreeText = !term.forceFreeText">
                Toggle free text
              </button>
            </div>
          </label>
          <typed-control
            [id]="'partition' + i"
            [input]="partition"
            [control]="form.get(partition.name)"
            [clearButton]="false"
            [disableRestrictions]="term.forceFreeText"
            (change)="supply(partition)"
            (keydown.enter)="supply(partition, true)">
          </typed-control>
        </div>
      </div>

      <template [ngIf]="filters.length > 0">
        <hr/>
        <h4>Filters</h4>
      </template>
      <div
        *ngFor="let filter of filters; let i = index"
        class="row fieldEntry"
        @fadeInOut>
        <div class="col-xs-12">
          <table width="100%">
            <tr>
              <th>
                {{filter.displayName | uppercase}}
              </th>
              <td>
                <select
                  *ngIf="filter.operators?.length > 1"
                  class="form-control input-sm"
                  [formControl]="opChoices.get(filter.name)">
                  <option
                    *ngFor="let op of filter.operators"
                    [ngValue]="op">
                    {{operatorLabel(filter, op)}}
                  </option>
                </select>
              </td>
            </tr>
          </table>
          <typed-control
            [id]="'filter' + i"
            [input]="substituteMagic(filter)"
            [control]="form.get(filter.name)"
            [clearButton]="false"
            (change)="supply(filter)"
            (keydown.enter)="supply(filter, true)">
          </typed-control>
        </div>
      </div>

      <table *ngIf="usingTerminalOperator" class="table center-items text-info">
        <tr>
          <td>
            <h1><span class="glyphicon glyphicon-info-sign"></span></h1>
          </td>
          <td>
            Tip: you can only add an "inexact" selection (such as greater than,
            less than, before, or after) as the last item in your search. If
            you wish to add other "exact" selections, please add them first.
          </td>
        </tr>
      </table>

      <div class="row">
        <div class="col-xs-6 form-group">
          <button
            class="btn btn-default"
            (click)="clear()">
            Clear Query
          </button>
        </div>
        <div class="col-xs-6 form-group">
          <button
            type="submit"
            class="btn btn-primary"
            (click)="submit()"
            [disabled]="!canSearch">
            Search
          </button>
        </div>
      </div>
    </div>

`,
  styles: [
    `.fieldEntry {
      overflow: hidden;
    }
    .searchTerm label {
      width: 100%;
    }
    .fieldEntry >>> .form-group {
      margin-top: 0px;
    }
    .well {
      padding: 5px 19px;
    }
    h4 {
      font-style: italic;
    }
    .form-group > button {
      width: 100%;
    }
    .well-top {
      margin: auto -14px;
    }
    td>.btn:only-child {
      width: 100%;
    }`
  ],
  animations: [
    trigger("fadeInOut", [])
    //    [
    //     transition("void => *", [
    //       style({height: 0, opacity: 0}),
    //       animate(ANIMATION_SPEED_IN, style({height: "*", opacity: 1}))
    //     ]),
    //     transition("* => void", [
    //       style({height: "*", opacity: 1}),
    //       animate(ANIMATION_SPEED_OUT, style({height: 0, opacity: 0}))
    //     ])
    //   ])
  ]
})
export class SearchPanelAlternativeComponent implements OnInit
{
  public form : FormGroup;
  public opChoices : FormGroup;
  public currentIndex : FormControl;
  public partitions : PartitionNode[] = [];
  public filters : FilterNode[] = [];
  private path : string[] = [];
  public canSearch : boolean = false;

  public allowsFreeTextOverride : Set<string> = new Set(["case handler"]);

  @Input()
  public enforcedType : SubjectContextType;

  @Input()
  public unwrap : boolean = false;

  @Output()
  public onSubmit : EventEmitter<[SubjectContextType, SearchTerm[]]> =
    new EventEmitter();

  public usingTerminalOperator : boolean = false;

  /**
   * Construct a new {@link SearchComponent}.
   *
   */
  public constructor (
    public search : SearchService,
    public alert : AlertService
  )
  {
    // Nothing
  }

  public opts : SearchNode[][] = [];

  public ngOnInit () : void
  {
    this.form = new FormGroup({});
    this.opChoices = new FormGroup({});
    this.currentIndex = new FormControl();
    this.currentIndex.valueChanges.subscribe(() => {
      this.clear();
    });
    this.search.ready.subscribe((ready : boolean) => {
      if (ready)
      {
        // the value setting will trigger clear() [see above]
        this.opts =
          this.search.rootByType.get(
            this.search.availableTypes[0]
          ).generateFlatOptions([]);
        this.currentIndex.setValue(this.opts[0]);
      }
    });
  }

  public displayIndex (index : SearchNode[]) : string
  {
    return index.map(
      (n : SearchNode) =>
        (n instanceof SearchRoot) ? "(healthcare benefits)" : n.name
      ).join(" > ");
  }

  private clear () : void
  {
    this.canSearch = false;
    this.partitions = [];
    this.filters = [];
    this.path = [];
    this.generateControls();
  }

  private generateControls () : void
  {
    const index : SearchNode[] = this.currentIndex.value;
    if (Array.isArray(index))
    {
      index.forEach((node : SearchNode) => {
        if (node instanceof PartitionNode)
        {
          this.partitions.push(node);
          this.addControl(node);
        }
        else if (node instanceof FilterNode)
        {
          this.filters.push(node);
          this.addControl(node);
        }
      });
    }
    setTimeout(() => this.focusFirst(), 100);
  }

  private focusFirst () : void
  {
    const n : string = (this.partitions.length > 0) ? "partition0" : "filter0";
    const el : Element = document.querySelector(`#${n} .focusable`);
    if (el)
    {
    // annoying type mismatch between Element and HTMLElement I guess?
    (el as any).focus();
    }
  }

  private addControl (node : SearchNode) : void
  {
    if (!this.form.contains(node.name))
    {
      this.form.addControl(
        node.name,
        new FormControl()
      );
    }
    if (!this.opChoices.contains(node.name))
    {
      this.opChoices.addControl(
        node.name,
        new FormControl(node.operators[0])
      );
    }
    else if (node instanceof PartitionNode)
    {
      // We may have "leftover" controls from a filter node with the same name,
      // but since we aren't allowing selection of partition operator yet, we
      // set it to equality so that it isn't (invisibly) left as something else.
      this.opChoices.get(node.name).setValue(Operator.EQUALS);
    }
  }

  public supply (node : SearchNode, trySubmit : boolean = false) : void
  {
    if (empty(this.form.get(node.name).value))
    {
      // Don't add to the if the field's value is empty
      return;
    }
    const opField : AbstractControl = this.opChoices.get(node.name);
    if (opField && valueless(opField.value))
    {
      console.info(opField);
      // Don't add to query if it has an unchosen operator
      return;
    }
    if (this.path.indexOf(node.name) > -1)
    {
      // Don't add to query if it's already there
      return;
    }
    this.path.push(node.name);

    this.canSearch = this.allFilled();
    if (trySubmit && this.canSearch)
    {
      console.log("Attempting submit");
      this.submit();
    }
  }

  private allFilled () : boolean
  {
    this.path.length = this.partitions.length + this.filters.length;
    // TODO!
    return true;
  }

  private getType () : SubjectContextType
  {
    return this.enforcedType || this.currentIndex.value;
  }

  public submit () : void
  {
    const terms : SearchTerm[] = this.path.map((name : string) => {

        const opField : {value : OperatorString | MagicOperator<any>} =
          this.opChoices.get(name);
        if (opField)
        {
          if (opField.value instanceof MagicOperator)
          {
            return opField.value.transform(name, this.form.get(name).value);
          }
          return {
            name,
            operator: opField.value,
            value: this.form.get(name).value
          };
        }
        else
        {
          return {
            name,
            operator: Operator.EQUALS,
            value: this.form.get(name).value
          };
        }
    });
    this.onSubmit.emit([this.getType(), terms]);
    this.search.runQuery(this.getType(), terms);
  }

  public substituteMagic (filter : FilterNode) : TypedDataHolder
  {
    const op : AbstractControl = this.opChoices.get(filter.name);
    if (op && op.value instanceof MagicOperator)
    {
      return {
        name: `${filter.name}-${op.value.name}`,
        type: op.value.argumentType,
        cardinality: 1, // only support scalars ATM
        value: null,
        restrictions: [],
        hints: {}
      };
    }
    return filter;
  }

  // public isMagical (filter : FilterNode) : boolean
  // {
  //   const op : AbstractControl = this.opChoices.get(filter.name);
  //   return (op && op.value instanceof MagicOperator);
  // }

  private hasOpValue (node : SearchNode) : boolean
  {
    return (!!this.opChoices.get(node.name));
  }

  private operatorLabel (node : SearchNode, op? : OperatorString) : string
  {
    if (!op)
    {
      if (!this.opChoices.get(node.name))
      {
        throw Error(`Could not find operator selection for ${node.name}`);
      }
      op = this.opChoices.get(node.name).value;
    }
    return Operator.labelFor(op, node);
  }

  public runRecentQuery (i : number) : void
  {
    // we're using the reversed array, so we need the reverse index
    this.search.runRecentQuery(this.search.recentSearches.size() - i - 1);
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/search/search-panel-alternative.component.ts